<template>
  <div class="dash">
    <div class="dash__tiles">
      <StatTile label="Naplaćeno" :value="money(d.naplaceno)"
        :meta="fmt(d.novi) + ' novih · ' + fmt(d.zatvoreni) + ' zatvorenih'" />
      <StatTile label="Isplaćeno" :value="money(d.isplaceno)" meta="klijentima i otkupi" />
      <StatTile tone="accent" label="Gotovinski tok" :value="money(d.profit)" />
      <StatTile tone="danger" label="U zastoju" :value="fmt(d.stale.zastoj)"
        :meta="pct(d.stale.zastoj, d.stale.otvoreno) + '% od ' + fmt(d.stale.otvoreno) + ' otvorenih'" />
    </div>

    <div class="dash__body">
      <section class="card dash__main">
        <h2 class="card__title">Učinak tima</h2>
        <div class="card__hint">
          novi, zatvorio i naplatio su za odabrani period · „otvoreni sada” je trenutno stanje
        </div>
        <div class="team">
          <div class="team__row team__row--head">
            <div>Osoba</div>
            <div>Udio u naplati</div>
            <div class="team__right">Otvoreni <span class="team__now">sada</span></div>
            <div class="team__right">Novi</div>
            <div class="team__right">Zatvorio</div>
            <div class="team__right">Naplatio</div>
          </div>
          <div v-for="t in d.tim" :key="t.ime" class="team__row">
            <div class="team__name">{{ t.ime }}</div>
            <div class="row__bar"><span class="row__fill" :style="{ width: share(t.naplatio) + '%' }"></span></div>
            <div class="team__right">{{ fmt(t.otvoreni) }}</div>
            <div class="team__right">{{ fmt(t.novi) }}</div>
            <div class="team__right">{{ fmt(t.zatvorio) }}</div>
            <div class="team__right team__money">{{ money(t.naplatio) }}</div>
          </div>
        </div>
      </section>

      <aside class="dash__rail">
        <section class="card">
          <h2 class="card__title">Otkup — portfelj</h2>
          <div class="card__hint">otkupi u odabranom periodu</div>
          <div style="margin-top: 12px">
            <div class="money">
              <span class="money__label">Uloženo</span>
              <span class="money__value">{{ money(d.otkup.ulozeno) }}</span>
            </div>
            <div class="money">
              <span class="money__label">Naplaćeno</span>
              <span class="money__value">{{ money(d.otkup.naplaceno) }}</span>
            </div>
            <div class="money">
              <span class="money__label">Razlika</span>
              <span class="money__value" :class="{ 'is-neg': diff < 0 }">{{ money(diff) }}</span>
            </div>
            <div class="money">
              <span class="money__label">Prinos</span>
              <span class="money__value" :class="{ 'is-neg': diff < 0 }">{{ yieldPct }}</span>
            </div>
          </div>
        </section>

        <section v-if="idle.predmeti" class="card card--warn">
          <h2 class="card__title">Predmeti kod neaktivnih</h2>
          <div class="card__hint">
            {{ fmt(idle.predmeti) }} otvorenih predmeta kod korisnika bez aktivnosti u periodu — kandidati za preraspodjelu
          </div>
          <div class="idle">
            <div v-for="u in idle.korisnici" :key="u.ime" class="idle__row">
              <span class="idle__name">{{ u.ime }}</span>
              <strong class="idle__count">{{ u.otvoreni }}</strong>
              <span class="idle__last">{{ u.zadnja_aktivnost ? "zadnja aktivnost " + u.zadnja_aktivnost : "bez aktivnosti" }}</span>
            </div>
          </div>
        </section>

        <section class="card">
          <h2 class="card__title">Rizik — zastoji po fazi</h2>
          <div class="card__hint">otvoreni predmeti bez pomaka 30+ dana</div>
          <div class="phases">
            <div v-for="p in topStale" :key="p.status" class="phase">
              <span class="phase__dot" :style="{ background: color(p.status) }"></span>
              <span class="phase__name">{{ label(p.status) }}</span>
              <strong class="phase__count">{{ p.zastoj }}</strong>
              <span class="phase__of">/ {{ p.ukupno }}</span>
            </div>
          </div>
          <router-link v-if="canReports" to="/reports" class="card__link">Otvori izvještaje →</router-link>
        </section>
      </aside>
    </div>
  </div>
</template>

<script>
import StatTile from "./StatTile";
import { getStatus, getStatusLabel } from "@/models/status";
import { canViewReports } from "@/models/admin";

export default {
  name: "OwnerDash",
  components: { StatTile },
  props: { data: { type: Object, required: true } },
  computed: {
    d() {
      return this.data;
    },
    diff() {
      return (this.d.otkup.naplaceno || 0) - (this.d.otkup.ulozeno || 0);
    },
    yieldPct() {
      const inv = this.d.otkup.ulozeno || 0;
      if (!inv) return "—";
      const p = (this.diff / inv) * 100;
      return (p >= 0 ? "+" : "") + p.toFixed(1) + "%";
    },
    canReports() {
      return canViewReports();
    },
    idle() {
      return this.d.neaktivni || { korisnici: [], predmeti: 0 };
    },
    maxCollected() {
      return Math.max.apply(null, (this.d.tim || []).map((t) => t.naplatio).concat([1]));
    },
    topStale() {
      return (this.d.stale.po_fazi || []).filter((p) => p.zastoj > 0).slice(0, 6);
    },
  },
  methods: {
    fmt(v) {
      return String(Math.round(Number(v) || 0)).replace(/\B(?=(\d{3})+(?!\d))/g, ".");
    },
    money(v) {
      const parts = Number(v || 0).toFixed(2).split(".");
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ".");
      return parts.join(",");
    },
    pct(a, b) {
      if (!b) return 0;
      return Math.round((a / b) * 100);
    },
    share(v) {
      return Math.max((v / this.maxCollected) * 100, 1);
    },
    label: getStatusLabel,
    color(id) {
      return getStatus(id).dot;
    },
  },
};
</script>

<style lang="scss" scoped>
@import "./dash.scss";

.dash__main {
  overflow-y: auto;
}

.team {
  margin-top: 12px;
}

.team__row {
  display: grid;
  grid-template-columns: 150px minmax(0, 1fr) 80px 80px 80px 120px;
  gap: 12px;
  align-items: center;
  padding: 9px 0;
  border-bottom: 1px solid var(--line-soft);
  font-size: var(--fs-md);
}

.team__row--head {
  font-size: var(--fs-xs);
  font-weight: 600;
  color: var(--ink-4);
  text-transform: uppercase;
  letter-spacing: 0.04em;
  border-bottom: 1px solid var(--line);
}

.team__name {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.team__right {
  text-align: right;
  font-variant-numeric: tabular-nums;
}

.team__now {
  font-weight: 400;
  text-transform: none;
  letter-spacing: 0;
  color: var(--ink-4);
}

.team__money {
  font-weight: 600;
}

.card--warn {
  border-color: #ecdcae;
  background: var(--warn-tint);

  .card__hint {
    color: var(--warn);
  }
}

.idle {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 12px;
}

.idle__row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 34px;
  gap: 8px;
  align-items: baseline;
  font-size: var(--fs-sm);
}

.idle__name {
  color: var(--ink);
  font-weight: 500;
}

.idle__count {
  text-align: right;
  font-variant-numeric: tabular-nums;
}

.idle__last {
  grid-column: 1 / -1;
  font-size: var(--fs-xs);
  color: var(--ink-3);
  margin-top: -4px;
}

.phase__of {
  font-size: var(--fs-xs);
  color: var(--ink-4);
  width: 40px;
  text-align: right;
}

.is-neg {
  color: var(--danger);
}

.card__link {
  display: inline-block;
  margin-top: 12px;
  font-size: var(--fs-sm);
  color: var(--brand-ink);
  text-decoration: none;
}
</style>
